import { X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { DOUBLE_TAP_MS } from "../viewport/interactionConstants.ts";
import { IconButton } from "./Button.tsx";
import {
  PANEL_SURFACE_HEADER_HEIGHT,
  PANEL_SURFACE_RADIUS,
  PanelSurfaceBody,
  PanelSurfaceFrame,
  PanelSurfaceHeader,
} from "./panelSurface.tsx";
import { COLLAPSE_MS, COLOR_TEXT_FAINT, RADIUS_XS } from "./tokens.ts";

interface Position {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// localStorage-backed caches
// ---------------------------------------------------------------------------

const POS_KEY = "poseref-panel-positions";
const COL_KEY = "poseref-panel-collapsed-drag";

function loadMap<T>(key: string): Map<string, T> {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return new Map(Object.entries(JSON.parse(raw)));
  } catch {
    /* corrupted — start fresh */
  }
  return new Map();
}

function saveMap<T>(key: string, map: Map<string, T>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Object.fromEntries(map)));
  } catch {
    /* storage full — ignore */
  }
}

/** Module-level cache so positions survive unmount/remount cycles and refresh. */
const positionCache = loadMap<Position>(POS_KEY);

/** Module-level cache so collapsed state survives unmount/remount cycles and refresh. */
const collapsedCache = loadMap<boolean>(COL_KEY);

const SIZE_KEY = "poseref-panel-sizes";
const sizeCache = loadMap<{ width: number; height: number }>(SIZE_KEY);

function savePosition(id: string, pos: Position): void {
  positionCache.set(id, pos);
  saveMap(POS_KEY, positionCache);
}

function saveCollapsed(id: string, collapsed: boolean): void {
  collapsedCache.set(id, collapsed);
  saveMap(COL_KEY, collapsedCache);
}

function saveSize(id: string, size: { width: number; height: number }): void {
  sizeCache.set(id, size);
  saveMap(SIZE_KEY, sizeCache);
}

/**
 * Global z-index counter.  Each interaction (tap or drag) on a panel bumps
 * it to the top of the stack.  Not persisted — stacking order resets on refresh.
 */
let zCounter = 0;

/** Current highest z-index used by any DraggablePanel (base + bump). */
export function getTopPanelZ(): number {
  return zCounter;
}
const zOrderCache = new Map<string, number>();

const DRAG_THRESHOLD = 5;

// COLLAPSE_MS imported from tokens.ts

export interface DraggablePanelProps {
  /** Unique key used to persist position across unmount/remount cycles. */
  id: string;
  /** Initial CSS position — supply the same anchor corner props you'd normally use. */
  defaultPosition: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  /** z-index for the panel. */
  zIndex?: number;
  /** Extra styles merged onto the outer wrapper. */
  style?: CSSProperties;
  /** Optional managed body chrome styles. When provided, DraggablePanel wraps `children` in this body container. */
  bodyStyle?: CSSProperties;
  /** Called when the user clicks the built-in close button. Omit to hide the button. */
  onClose?: () => void;
  /** Keep the panel's drag position when it unmounts and restore on remount.  @default true */
  persistPosition?: boolean;
  /**
   * When true, double-tapping the header collapses the panel to a compact
   * icon-only coin.  Tapping the collapsed coin expands it again.
   * Requires the `header` prop.
   *
   * DraggablePanel owns the glass chrome, padding, and typography for the
   * header — consumers just supply the content (icon + label).
   */
  collapsible?: boolean;
  /**
   * Header content placed inside the managed drag-handle bar.
   * Can be a ReactNode or a render function receiving collapsed state
   * (for cases where the header content itself needs to vary).
   *
   * The outer glass chrome, padding, border radii, font size, and
   * min-height are all handled by DraggablePanel — don't duplicate them.
   */
  header?: ReactNode | ((collapsed: boolean) => ReactNode);
  /**
   * Optional accent color that subtly tints the glass header.
   * Pass a CSS color string (e.g. "rgba(200,118,224,0.08)").
   */
  tint?: string;
  /** When true, renders a selection-highlight border around the panel. */
  selected?: boolean;
  /** Accent color for the selection highlight (defaults to white). */
  selectionColor?: string;
  /** Called on any pointer-down inside the panel (for focus/selection tracking). */
  onSelect?: () => void;
  /**
   * Extra action buttons rendered alongside the close button in the
   * top-right corner of the header.  Rendered left of the close button.
   * Each button should use `data-no-drag` to avoid triggering drag.
   */
  headerActions?: ReactNode;
  /** Enable edge/corner resize handles.  @default false */
  resizable?: boolean;
  /** Default size (width x height).  Used when resizable is true and no persisted size exists. */
  defaultSize?: { width: number; height: number };
  /** Minimum size constraints when resizable. */
  minSize?: { width: number; height: number };
  /** Maximum size constraints when resizable. */
  maxSize?: { width: number; height: number };
  children: ReactNode;
}

/**
 * Generic draggable wrapper for overlay panels.
 *
 * Renders an absolutely-positioned container that can be repositioned by
 * dragging a handle (any child element with `data-drag-handle`).
 *
 * The panel is clamped to the viewport on every move and on window resize.
 * When the panel's content is too tall to fit, the body scrolls.
 */
export function DraggablePanel({
  id,
  defaultPosition,
  zIndex = 15,
  style,
  onClose,
  persistPosition = true,
  collapsible = false,
  header,
  tint,
  selected = false,
  selectionColor,
  onSelect,
  resizable = false,
  defaultSize,
  minSize,
  maxSize,
  headerActions,
  bodyStyle,
  children,
}: DraggablePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const dragOffset = useRef<Position>({ x: 0, y: 0 });
  const lastTapTime = useRef(0);

  // Restore cached position on mount, or null to trigger initial layout read.
  const [pos, setPos] = useState<Position | null>(
    persistPosition ? (positionCache.get(id) ?? null) : null,
  );

  const collapseEnabled = collapsible && header != null;

  const [collapsed, setCollapsed] = useState(
    collapseEnabled ? (collapsedCache.get(id) ?? false) : false,
  );

  // Resizable size state — restored from cache or defaultSize.
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    resizable ? (sizeCache.get(id) ?? defaultSize ?? null) : null,
  );

  // Brief highlight pulse on the header after collapsing.
  const [pulsing, setPulsing] = useState(false);
  const prevCollapsed = useRef(collapsed);
  useEffect(() => {
    if (collapsed && !prevCollapsed.current) {
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 400);
      return () => clearTimeout(t);
    }
    prevCollapsed.current = collapsed;
  }, [collapsed]);

  // Body max-height: undefined = unconstrained, number = capped to fit viewport.
  const [bodyMaxHeight, setBodyMaxHeight] = useState<number | undefined>(
    undefined,
  );

  // Z-order: higher = on top.  Bumped on mount and every interaction.
  const [zBump, setZBump] = useState(() => {
    const next = ++zCounter;
    zOrderCache.set(id, next);
    return next;
  });
  const bringToFront = useCallback(() => {
    const next = ++zCounter;
    zOrderCache.set(id, next);
    setZBump(next);
  }, [id]);

  // Persist collapsed state.
  useEffect(() => {
    if (collapseEnabled) saveCollapsed(id, collapsed);
  }, [id, collapseEnabled, collapsed]);

  // On first mount (no cached pos), read the browser-laid-out rect.
  useEffect(() => {
    const el = panelRef.current;
    if (!el || pos !== null) return;

    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const resolved = { x: rect.left, y: rect.top };
      setPos(resolved);
      if (persistPosition) savePosition(id, resolved);
    });
  }, [pos, id, persistPosition]);

  // Persist position on every change.
  useEffect(() => {
    if (pos && persistPosition) savePosition(id, pos);
  }, [pos, id, persistPosition]);

  const clamp = useCallback((p: Position): Position => {
    const el = panelRef.current;
    if (!el) return p;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
      x: Math.max(0, Math.min(p.x, vw - rect.width)),
      y: Math.max(0, Math.min(p.y, vh - rect.height)),
    };
  }, []);

  // Compute body max-height so the panel never extends beyond the viewport.
  const updateBodyMaxHeight = useCallback(() => {
    if (collapsed) {
      setBodyMaxHeight(undefined);
      return;
    }
    const el = panelRef.current;
    if (!el) return;
    const vh = window.innerHeight;
    const headerH =
      headerRef.current?.getBoundingClientRect().height ??
      PANEL_SURFACE_HEADER_HEIGHT;
    const panelY = pos?.y ?? el.getBoundingClientRect().top;
    // Available space for the body = viewport bottom - panel top - header height
    const available = vh - panelY - headerH;
    // Only constrain if the body would overflow
    setBodyMaxHeight(available > 0 ? available : 100);
  }, [pos, collapsed]);

  // Re-clamp on window resize.
  useEffect(() => {
    const onResize = () => {
      setPos((prev) => (prev ? clamp(prev) : prev));
      updateBodyMaxHeight();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp, updateBodyMaxHeight]);

  // Re-clamp + recalculate body height after collapse/expand.
  useEffect(() => {
    requestAnimationFrame(() => {
      setPos((prev) => (prev ? clamp(prev) : prev));
      updateBodyMaxHeight();
    });
  }, [clamp, updateBodyMaxHeight]);

  // Recalculate body max-height when position changes (e.g. after drag).
  useEffect(() => {
    updateBodyMaxHeight();
  }, [updateBodyMaxHeight]);

  // --- Resize -----------------------------------------------------------
  type ResizeEdge = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

  const onResizeStart = useCallback(
    (edge: ResizeEdge, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      bringToFront();
      if (!size || !pos) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const startSize = { ...size };
      const startPos = { ...pos };
      const minW = minSize?.width ?? 160;
      const minH = minSize?.height ?? 80;
      const maxW = maxSize?.width ?? window.innerWidth;
      const maxH = maxSize?.height ?? window.innerHeight;

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let newW = startSize.width;
        let newH = startSize.height;
        let newX = startPos.x;
        let newY = startPos.y;

        if (edge.includes("e")) newW = startSize.width + dx;
        if (edge.includes("w")) {
          newW = startSize.width - dx;
          newX = startPos.x + dx;
        }
        if (edge.includes("s")) newH = startSize.height + dy;
        if (edge.includes("n")) {
          newH = startSize.height - dy;
          newY = startPos.y + dy;
        }

        // Clamp size
        newW = Math.max(minW, Math.min(maxW, newW));
        newH = Math.max(minH, Math.min(maxH, newH));

        // If west/north clamped the size, adjust position accordingly
        if (edge.includes("w")) newX = startPos.x + (startSize.width - newW);
        if (edge.includes("n")) newY = startPos.y + (startSize.height - newH);

        setSize({ width: newW, height: newH });
        setPos(clamp({ x: newX, y: newY }));
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setSize((s) => {
          if (s) saveSize(id, s);
          return s;
        });
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [size, pos, clamp, bringToFront, id, minSize, maxSize],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      const handle = target.closest("[data-drag-handle]");
      if (!handle) return;

      // Don't drag if user clicked an interactive element inside the handle.
      if (target.closest("button, input, select, textarea, [data-no-drag]"))
        return;

      e.preventDefault();
      e.stopPropagation();
      dragging.current = true;
      didDrag.current = false;
      bringToFront();

      const el = panelRef.current!;
      const rect = el.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragging.current) return;
        // Track whether pointer moved significantly (to distinguish tap vs drag)
        if (
          !didDrag.current &&
          (Math.abs(ev.clientX - startX) > DRAG_THRESHOLD ||
            Math.abs(ev.clientY - startY) > DRAG_THRESHOLD)
        ) {
          didDrag.current = true;
        }
        const next = {
          x: ev.clientX - dragOffset.current.x,
          y: ev.clientY - dragOffset.current.y,
        };
        setPos(clamp(next));
      };

      const onPointerUp = () => {
        dragging.current = false;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);

        // Double-tap detection (only if no drag occurred)
        if (!didDrag.current && collapseEnabled) {
          const now = Date.now();
          if (now - lastTapTime.current < DOUBLE_TAP_MS) {
            setCollapsed((prev) => !prev);
            lastTapTime.current = 0;
          } else {
            lastTapTime.current = now;
          }
        }

        // If collapsed, single tap (no drag) expands
        if (!didDrag.current && collapsed) {
          setCollapsed(false);
        }
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [clamp, collapseEnabled, collapsed, bringToFront],
  );

  // Before the first layout-read we render with the caller's anchor styles
  // so the browser can position the element for us to measure.
  const anchorStyle: CSSProperties = pos
    ? { top: pos.y, left: pos.x }
    : {
        ...(defaultPosition.top != null && { top: defaultPosition.top }),
        ...(defaultPosition.right != null && { right: defaultPosition.right }),
        ...(defaultPosition.bottom != null && {
          bottom: defaultPosition.bottom,
        }),
        ...(defaultPosition.left != null && { left: defaultPosition.left }),
      };

  const hasHeader = header != null;
  const headerContent = hasHeader
    ? typeof header === "function"
      ? header(collapsed)
      : header
    : null;

  const selColor = selectionColor || "rgba(255,255,255,0.5)";
  const selectionShadow = selected
    ? `0 0 0 1.5px ${selColor}, 0 0 12px 2px ${selColor.replace(/[\d.]+\)$/, "0.15)")}`
    : undefined;
  const dimAtSurface = bodyStyle != null;
  const bodyChildren = bodyStyle ? (
    <PanelSurfaceBody style={bodyStyle}>{children}</PanelSurfaceBody>
  ) : (
    children
  );

  return (
    <div
      ref={panelRef}
      data-draggable-panel={id}
      data-no-ctx
      onPointerDown={onPointerDown}
      onPointerDownCapture={onSelect}
      style={{
        position: "absolute",
        zIndex: (zIndex ?? 15) + zBump,
        ...anchorStyle,
        ...(collapsed ? { width: "fit-content" } : style),
        ...(resizable && size && !collapsed
          ? {
              width: size.width,
              height: size.height,
              display: "flex",
              flexDirection: "column" as const,
            }
          : {}),
        borderRadius: PANEL_SURFACE_RADIUS,
        boxShadow: selectionShadow,
      }}
    >
      {!hasHeader && !dimAtSurface && (
        <PanelSurfaceFrame style={style}>{children}</PanelSurfaceFrame>
      )}

      {/* Managed header — glass chrome owned by DraggablePanel */}
      {hasHeader && (
        <PanelSurfaceHeader
          ref={headerRef}
          tint={tint}
          collapsed={collapsed}
          pulsing={pulsing}
          style={{
            padding: `12px ${headerActions && onClose ? 58 : (headerActions || onClose) ? 36 : 14}px 12px 14px`,
          }}
        >
          {headerContent}
        </PanelSurfaceHeader>
      )}

      {/* Body — animates closed via grid-template-rows; scrolls when too tall */}
      {hasHeader ? (
        <div
          style={{
            display: resizable && size && !collapsed ? "flex" : "grid",
            flexDirection: "column",
            gridTemplateRows: collapsed ? "0fr" : "1fr",
            // width: 0 + min-width: 100% ensures this element follows
            // the parent's width rather than pushing it wider — so
            // fit-content on the outer wrapper measures only the header.
            width: 0,
            minWidth: "100%",
            overflow: "hidden",
            flex: resizable && size && !collapsed ? 1 : undefined,
            transition: collapsed
              ? "none"
              : `grid-template-rows ${COLLAPSE_MS}ms ease`,
          }}
        >
          <div
            style={{
              overflow: "hidden",
              minHeight: 0,
              maxHeight: !collapsed
                ? resizable && size
                  ? undefined
                  : bodyMaxHeight
                : undefined,
              overflowY: !collapsed ? "auto" : undefined,
              flex: resizable && size && !collapsed ? 1 : undefined,
            }}
          >
            {bodyChildren}
          </div>
        </div>
      ) : dimAtSurface ? (
        bodyChildren
      ) : null}

      {/* Header action buttons — fade out when collapsed */}
      {(headerActions || onClose) && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            alignItems: "center",
            gap: 2,
            opacity: collapsed ? 0 : 1,
            pointerEvents: collapsed ? "none" : "auto",
            transition: collapsed ? "none" : `opacity ${COLLAPSE_MS}ms ease`,
          }}
        >
          {headerActions}
          {onClose && (
            <IconButton
              onClick={onClose}
              data-no-drag
              label="Close panel"
              icon={<X size={14} />}
              size={22}
              radius={RADIUS_XS}
              style={{ color: COLOR_TEXT_FAINT }}
            />
          )}
        </div>
      )}

      {/* Resize handles — invisible edge/corner hit targets */}
      {resizable && size && !collapsed && (
        <>
          <div
            onPointerDown={(e) => onResizeStart("n", e)}
            style={{
              position: "absolute",
              top: -4,
              left: 12,
              right: 12,
              height: 8,
              cursor: "ns-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("s", e)}
            style={{
              position: "absolute",
              bottom: -4,
              left: 12,
              right: 12,
              height: 8,
              cursor: "ns-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("e", e)}
            style={{
              position: "absolute",
              top: 12,
              right: -4,
              bottom: 12,
              width: 8,
              cursor: "ew-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("w", e)}
            style={{
              position: "absolute",
              top: 12,
              left: -4,
              bottom: 12,
              width: 8,
              cursor: "ew-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("nw", e)}
            style={{
              position: "absolute",
              top: -4,
              left: -4,
              width: 14,
              height: 14,
              cursor: "nwse-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("ne", e)}
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              width: 14,
              height: 14,
              cursor: "nesw-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("sw", e)}
            style={{
              position: "absolute",
              bottom: -4,
              left: -4,
              width: 14,
              height: 14,
              cursor: "nesw-resize",
              touchAction: "none",
            }}
          />
          <div
            onPointerDown={(e) => onResizeStart("se", e)}
            style={{
              position: "absolute",
              bottom: -4,
              right: -4,
              width: 14,
              height: 14,
              cursor: "nwse-resize",
              touchAction: "none",
            }}
          />
        </>
      )}
    </div>
  );
}
